/**
 * Cart Page & Drawer JavaScript
 * Handles cart interactions, AJAX updates, and drawer functionality
 */

(function() {
  'use strict';

  /**
   * Cart Drawer
   */
  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('[data-cart-drawer-overlay]');
      this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
      this.content = this.drawer.querySelector('[data-cart-drawer-content]');

      this.init();
    }

    init() {
      // Close button
      this.closeBtn?.addEventListener('click', () => this.close());

      // Overlay click
      this.overlay?.addEventListener('click', () => this.close());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Listen for cart open event (from product page add to cart)
      document.addEventListener('milutin:cart:open', () => this.open());

      // Cart icon click
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Drawer quantity and remove buttons
      this.setupDrawerControls();
    }

    isOpen() {
      return this.drawer.getAttribute('aria-hidden') === 'false';
    }

    open() {
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');

      // Focus close button
      this.closeBtn?.focus();

      // Refresh cart content
      this.refresh();
    }

    close() {
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
    }

    setupDrawerControls() {
      // Quantity minus
      this.drawer.addEventListener('click', async (e) => {
        const minusBtn = e.target.closest('[data-drawer-quantity-minus]');
        if (minusBtn) {
          const lineKey = minusBtn.getAttribute('data-line-key');
          const item = minusBtn.closest('[data-cart-drawer-item]');
          const valueEl = item?.querySelector('[data-drawer-quantity-value]');
          const currentQty = parseInt(valueEl?.textContent) || 1;

          if (currentQty > 1) {
            await this.updateItem(lineKey, currentQty - 1);
          } else {
            await this.removeItem(lineKey);
          }
        }
      });

      // Quantity plus
      this.drawer.addEventListener('click', async (e) => {
        const plusBtn = e.target.closest('[data-drawer-quantity-plus]');
        if (plusBtn) {
          const lineKey = plusBtn.getAttribute('data-line-key');
          const item = plusBtn.closest('[data-cart-drawer-item]');
          const valueEl = item?.querySelector('[data-drawer-quantity-value]');
          const currentQty = parseInt(valueEl?.textContent) || 1;

          await this.updateItem(lineKey, currentQty + 1);
        }
      });

      // Remove item
      this.drawer.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('[data-drawer-remove-item]');
        if (removeBtn) {
          const lineKey = removeBtn.getAttribute('data-line-key');
          await this.removeItem(lineKey);
        }
      });
    }

    async updateItem(lineKey, quantity) {
      this.setLoading(true);
      this.clearError();

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
        });

        const data = await response.json().catch(() => null);

        if (response.ok) {
          await this.refresh();
        } else {
          const message = (data && (data.description || data.message)) || 'Error updating cart';
          this.showError(message);
          await this.refresh();
        }
      } catch (error) {
        console.error('Error updating cart:', error);
        this.showError('Error updating cart');
      } finally {
        this.setLoading(false);
      }
    }

    showError(message) {
      if (!this.drawer) return;
      let errorEl = this.drawer.querySelector('[data-cart-drawer-error]');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.setAttribute('data-cart-drawer-error', '');
        errorEl.className = 'cart-drawer__error';
        errorEl.setAttribute('role', 'alert');
        const footer = this.drawer.querySelector('[data-cart-drawer-footer]');
        if (footer) {
          footer.insertBefore(errorEl, footer.firstChild);
        } else {
          this.drawer.appendChild(errorEl);
        }
      }
      errorEl.textContent = message;
    }

    clearError() {
      const errorEl = this.drawer?.querySelector('[data-cart-drawer-error]');
      if (errorEl) errorEl.remove();
    }

    async removeItem(lineKey) {
      await this.updateItem(lineKey, 0);
    }

    async refresh() {
      try {
        // Fetch fresh cart drawer HTML and the cart JSON in parallel
        const [sectionResp, cartResp] = await Promise.all([
          fetch('/?section_id=cart-drawer'),
          fetch('/cart.js')
        ]);
        const html = await sectionResp.text();
        const cart = await cartResp.json();

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newDrawer = doc.querySelector('[data-cart-drawer]');
        if (newDrawer && this.drawer) {
          const newPanel = newDrawer.querySelector('.cart-drawer__panel');
          const currentPanel = this.drawer.querySelector('.cart-drawer__panel');
          if (newPanel && currentPanel) {
            currentPanel.innerHTML = newPanel.innerHTML;
          }
          this.closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
          this.closeBtn?.addEventListener('click', () => this.close());
        }

        this.updateHeaderCount(cart.item_count);
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }

    formatMoney(cents) {
      const currency = window.shopCurrency || 'RSD';
      const locale = window.shopLocale || 'sr';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency
        }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(2)} ${currency}`;
      }
    }

    setLoading(loading) {
      this.drawer.classList.toggle('cart-drawer--loading', loading);
    }
  }

  /**
   * Cart Page
   */
  class CartPage {
    constructor() {
      this.form = document.querySelector('[data-cart-form]');
      if (!this.form) return;

      this.pendingUpdate = Promise.resolve();
      this.debounceTimers = new Map();

      this.init();
    }

    init() {
      this.form.querySelectorAll('[data-quantity-minus]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-cart-item]');
          const input = item?.querySelector('[data-quantity-input]');
          if (!input) return;
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            this.queueUpdate(input);
          }
        });
      });

      this.form.querySelectorAll('[data-quantity-plus]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-cart-item]');
          const input = item?.querySelector('[data-quantity-input]');
          if (!input) return;
          const currentValue = parseInt(input.value) || 1;
          input.value = currentValue + 1;
          this.queueUpdate(input);
        });
      });

      this.form.querySelectorAll('[data-quantity-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const value = parseInt(input.value) || 0;
          input.value = Math.max(0, value);
          this.queueUpdate(input);
        });
      });

      this.form.querySelectorAll('[data-remove-item]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const lineIndex = btn.getAttribute('data-line-index');
          this.runSerialized(() => this.sendChange(lineIndex, 0));
        });
      });
    }

    queueUpdate(input) {
      const lineIndex = input.getAttribute('data-line-index');
      clearTimeout(this.debounceTimers.get(lineIndex));
      const timer = setTimeout(() => {
        const quantity = parseInt(input.value) || 0;
        this.runSerialized(() => this.sendChange(lineIndex, quantity));
      }, 300);
      this.debounceTimers.set(lineIndex, timer);
    }

    runSerialized(task) {
      this.pendingUpdate = this.pendingUpdate.then(task, task);
      return this.pendingUpdate;
    }

    async sendChange(lineIndex, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: quantity })
        });

        if (response.ok) {
          location.reload();
        } else {
          const data = await response.json().catch(() => null);
          const message = (data && (data.description || data.message)) || 'Error updating cart';
          this.showError(message);
        }
      } catch (error) {
        console.error('Error updating cart:', error);
        this.showError('Error updating cart');
      }
    }

    showError(message) {
      let errorEl = this.form.querySelector('[data-cart-page-error]');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.setAttribute('data-cart-page-error', '');
        errorEl.className = 'cart-page__error';
        errorEl.setAttribute('role', 'alert');
        this.form.prepend(errorEl);
      }
      errorEl.textContent = message;
    }
  }

  /**
   * Initialize
   */
  function init() {
    new CartDrawer();
    new CartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
